import React, { useState, useEffect, useCallback, useRef } from 'react';
import { font } from '../theme';
import {
  Card, Table, Button, Space, Typography, message, Spin,
  Tooltip, Modal, Upload, Progress, Statistic, Row, Col,
  Popconfirm, Input, Form, Breadcrumb, Tag, List,
} from 'antd';
import type { UploadFile } from 'antd';
import {
  ReloadOutlined, CloudOutlined, DeleteOutlined,
  UploadOutlined, FileOutlined,
  CheckCircleOutlined, ExclamationCircleOutlined,
  DatabaseOutlined, FolderOutlined, InboxOutlined,
  FolderAddOutlined, HomeOutlined, RightOutlined,
  DownloadOutlined, CloseOutlined,
} from '@ant-design/icons';
import { storageAPI } from '../services/api';
import { brand, gray, radius, shadow, state } from '../theme/tokens';
import { useFitTableHeight } from '../hooks/useViewportFit';
import { fmtDateTime } from '../utils/date';

const { Title, Text } = Typography;

interface OciObject {
  name: string;
  size: number | null;
  time_modified: string | null;
  etag: string | null;
  is_folder: boolean;
}

interface StorageInfo {
  bucket: string;
  namespace: string;
  region: string;
  prefix: string;
  objects: OciObject[];
  total_count: number;
  total_size: number;
}

const formatBytes = (bytes: number | null): string => {
  if (!bytes) return '-';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const formatDate = (iso: string | null): string => fmtDateTime(iso);

const parseBreadcrumb = (prefix: string): { label: string; prefix: string }[] => {
  const parts = prefix.split('/').filter(Boolean);
  return parts.map((part, i) => ({
    label: part,
    prefix: parts.slice(0, i + 1).join('/') + '/',
  }));
};

const fileIconColor = (name: string): string => {
  const ext = name.split('.').pop()?.toLowerCase() || '';
  if (['hwp', 'hwpx'].includes(ext)) return '#3b82f6';
  if (['xlsx', 'xls'].includes(ext)) return state.success;
  if (['pdf'].includes(ext)) return state.error;
  if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) return brand.primary;
  return gray[400];
};

const StoragePage: React.FC = () => {
  const { tableWrapRef, cardWrapRef, bodyHeight } = useFitTableHeight();
  const [info, setInfo] = useState<StorageInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [healthy, setHealthy] = useState<boolean | null>(null);
  const [currentPrefix, setCurrentPrefix] = useState('');

  // 선택
  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);

  // 업로드
  const [uploadModal, setUploadModal] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<UploadFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });

  // 폴더 생성
  const [folderModal, setFolderModal] = useState(false);
  const [folderName, setFolderName] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);

  // 개별 삭제
  const [deletingKey, setDeletingKey] = useState<string | null>(null);

  // 일괄 작업 진행 상태
  const [bulkWorking, setBulkWorking] = useState(false);

  const downloadLinkRef = useRef<HTMLAnchorElement>(null);

  const fetchData = useCallback(async (prefix = currentPrefix) => {
    setLoading(true);
    try {
      const [healthRes, objRes] = await Promise.all([
        storageAPI.health(),
        storageAPI.listObjects(prefix, '/'),
      ]);
      setHealthy(healthRes.data.status === 'healthy');
      setInfo(objRes.data);
      setSelectedKeys([]);
    } catch {
      setHealthy(false);
      message.error('스토리지 정보를 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [currentPrefix]);

  useEffect(() => { fetchData(currentPrefix); }, [currentPrefix]);

  const navigateTo = (prefix: string) => {
    setCurrentPrefix(prefix);
    setSelectedKeys([]);
  };

  // ── 개별 삭제 ──
  const handleDelete = async (objectKey: string) => {
    setDeletingKey(objectKey);
    try {
      await storageAPI.deleteObject(objectKey);
      message.success(`삭제 완료: ${objectKey}`);
      fetchData(currentPrefix);
    } catch {
      message.error('삭제에 실패했습니다.');
    } finally {
      setDeletingKey(null);
    }
  };

  // ── 일괄 삭제 ──
  const handleBulkDelete = () => {
    Modal.confirm({
      title: `${selectedKeys.length}개 항목 삭제`,
      content: `선택한 ${selectedKeys.length}개 항목을 삭제합니다. 복구할 수 없습니다.`,
      okText: '삭제',
      okButtonProps: { danger: true },
      cancelText: '취소',
      onOk: async () => {
        setBulkWorking(true);
        let ok = 0;
        for (const key of selectedKeys) {
          try {
            await storageAPI.deleteObject(String(key));
            ok++;
          } catch {
            message.error(`삭제 실패: ${String(key).split('/').pop()}`);
          }
        }
        message.success(`${ok}/${selectedKeys.length}개 삭제 완료`);
        setSelectedKeys([]);
        setBulkWorking(false);
        fetchData(currentPrefix);
      },
    });
  };

  // ── 일괄 다운로드 ──
  const handleBulkDownload = async () => {
    const fileKeys = selectedKeys.filter(k => !String(k).endsWith('/'));
    if (!fileKeys.length) { message.warning('다운로드 가능한 파일이 없습니다 (폴더 제외)'); return; }
    setBulkWorking(true);
    let ok = 0;
    for (const key of fileKeys) {
      try {
        const res = await storageAPI.getDownloadUrl(String(key));
        const { url, filename } = res.data;
        // hidden anchor 클릭으로 다운로드 트리거
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.rel = 'noopener noreferrer';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        ok++;
        await new Promise(r => setTimeout(r, 400)); // 브라우저 다운로드 간격
      } catch {
        message.error(`다운로드 실패: ${String(key).split('/').pop()}`);
      }
    }
    message.success(`${ok}개 다운로드 시작`);
    setBulkWorking(false);
  };

  // ── 폴더 생성 ──
  const handleCreateFolder = async () => {
    const name = folderName.trim();
    if (!name) { message.warning('폴더명을 입력하세요.'); return; }
    setCreatingFolder(true);
    try {
      await storageAPI.createFolder(name, currentPrefix);
      message.success(`폴더 생성: ${name}/`);
      setFolderModal(false);
      setFolderName('');
      fetchData(currentPrefix);
    } catch {
      message.error('폴더 생성에 실패했습니다.');
    } finally {
      setCreatingFolder(false);
    }
  };

  // ── 파일 업로드 실행 ──
  const handleUpload = async () => {
    if (!pendingFiles.length) { message.warning('파일을 선택하세요.'); return; }
    setUploading(true);
    setUploadProgress({ current: 0, total: pendingFiles.length });
    let ok = 0;
    for (let i = 0; i < pendingFiles.length; i++) {
      const f = pendingFiles[i].originFileObj as File;
      setUploadProgress({ current: i + 1, total: pendingFiles.length });
      try {
        const formData = new FormData();
        formData.append('file', f);
        await storageAPI.uploadObject(formData, currentPrefix || undefined);
        ok++;
      } catch {
        message.error(`업로드 실패: ${f.name}`);
      }
    }
    message.success(`${ok}/${pendingFiles.length}개 업로드 완료`);
    setPendingFiles([]);
    setUploadModal(false);
    setUploading(false);
    fetchData(currentPrefix);
  };

  const displayName = (fullName: string): string => {
    if (currentPrefix && fullName.startsWith(currentPrefix)) {
      return fullName.slice(currentPrefix.length);
    }
    return fullName;
  };

  const breadcrumbSegments = parseBreadcrumb(currentPrefix);

  // 선택된 파일 수 (폴더 제외)
  const selectedFileCount = selectedKeys.filter(k => !String(k).endsWith('/')).length;
  const hasSelection = selectedKeys.length > 0;

  const columns = [
    {
      title: '이름',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: OciObject) => {
        const relName = displayName(name).replace(/\/$/, '');
        if (record.is_folder) {
          return (
            <Space style={{ cursor: 'pointer' }} onClick={() => navigateTo(name)}>
              <FolderOutlined style={{ color: '#f59e0b', fontSize: 16 }} />
              <Text style={{ fontSize: 13, color: brand.primary, fontWeight: 500 }}>{relName}</Text>
              <RightOutlined style={{ fontSize: 10, color: gray[400] }} />
            </Space>
          );
        }
        return (
          <Space>
            <FileOutlined style={{ color: fileIconColor(name) }} />
            <Text style={{ fontSize: 13 }}>{relName}</Text>
          </Space>
        );
      },
    },
    {
      title: '크기',
      dataIndex: 'size',
      key: 'size',
      width: 100,
      render: (size: number | null, record: OciObject) =>
        record.is_folder
          ? <Text style={{ fontSize: 12, color: gray[400] }}>폴더</Text>
          : <Text style={{ fontSize: 12, color: gray[500] }}>{formatBytes(size)}</Text>,
    },
    {
      title: '수정일',
      dataIndex: 'time_modified',
      key: 'time_modified',
      width: 160,
      render: (t: string | null) => (
        <Text style={{ fontSize: 12, color: gray[500] }}>{formatDate(t)}</Text>
      ),
    },
    {
      title: 'ETag',
      dataIndex: 'etag',
      key: 'etag',
      width: 100,
      render: (etag: string | null) => (
        <Text style={{ fontSize: 11, color: gray[400], fontFamily: font.mono }}>
          {etag ? etag.substring(0, 8) + '...' : '-'}
        </Text>
      ),
    },
    {
      title: '작업',
      key: 'actions',
      width: 100,
      render: (_: unknown, record: OciObject) => (
        <Space size={4}>
          {!record.is_folder && (
            <Tooltip title="다운로드">
              <Button
                type="text"
                size="small"
                icon={<DownloadOutlined />}
                style={{ color: brand.primary }}
                onClick={async () => {
                  try {
                    const res = await storageAPI.getDownloadUrl(record.name);
                    const a = document.createElement('a');
                    a.href = res.data.url;
                    a.download = res.data.filename;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                  } catch {
                    message.error('다운로드에 실패했습니다.');
                  }
                }}
              />
            </Tooltip>
          )}
          <Popconfirm
            title={record.is_folder ? '폴더 삭제' : '파일 삭제'}
            description={`"${displayName(record.name).replace(/\/$/, '')}" 을(를) 삭제합니까?`}
            onConfirm={() => handleDelete(record.name)}
            okText="삭제" cancelText="취소"
            okButtonProps={{ danger: true }}
          >
            <Button
              type="text" danger size="small"
              icon={<DeleteOutlined />}
              loading={deletingKey === record.name}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '20px 24px 0', background: gray[50], minHeight: '100%' }}>
      {/* 헤더 */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 40, height: 40, borderRadius: radius.lg,
            background: brand.primaryGradient,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: brand.primaryShadow,
          }}>
            <CloudOutlined style={{ color: '#fff', fontSize: 20 }} />
          </div>
          <div>
            <Title level={4} style={{ margin: 0, color: gray[800] }}>OCI 오브젝트 스토리지</Title>
            <Text style={{ fontSize: 12, color: gray[400] }}>Oracle Cloud Infrastructure Object Storage</Text>
          </div>
        </div>
        <Space>
          <Button icon={<FolderAddOutlined />} onClick={() => { setFolderName(''); setFolderModal(true); }}>
            폴더 생성
          </Button>
          <Button
            icon={<UploadOutlined />} type="primary"
            onClick={() => { setPendingFiles([]); setUploadModal(true); }}
            style={{ background: brand.primary, borderColor: brand.primary }}
          >
            파일 업로드
          </Button>
          <Tooltip title="새로고침">
            <Button icon={<ReloadOutlined />} onClick={() => fetchData(currentPrefix)} loading={loading} />
          </Tooltip>
        </Space>
      </div>

      {/* 통계 카드 */}
      <Row gutter={16} style={{ marginBottom: 20 }}>
        <Col span={6}>
          <Card style={{ borderRadius: radius.lg, border: `1px solid ${gray[200]}`, boxShadow: shadow.sm }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 36, height: 36, borderRadius: radius.md,
                background: healthy === true ? '#dcfce7' : healthy === false ? '#fee2e2' : gray[100],
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {healthy === true
                  ? <CheckCircleOutlined style={{ color: state.success, fontSize: 18 }} />
                  : healthy === false
                  ? <ExclamationCircleOutlined style={{ color: state.error, fontSize: 18 }} />
                  : <Spin size="small" />}
              </div>
              <div>
                <div style={{ fontSize: 11, color: gray[400] }}>연결 상태</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: healthy ? state.successText : state.errorText }}>
                  {healthy === null ? '확인 중...' : healthy ? '정상' : '오류'}
                </div>
              </div>
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card style={{ borderRadius: radius.lg, border: `1px solid ${gray[200]}`, boxShadow: shadow.sm }}>
            <Statistic
              title={<span style={{ fontSize: 11, color: gray[400] }}>현재 폴더 항목</span>}
              value={info?.total_count ?? 0}
              suffix="개"
              valueStyle={{ fontSize: 22, color: gray[800] }}
              prefix={<InboxOutlined style={{ color: brand.primary, marginRight: 4 }} />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card style={{ borderRadius: radius.lg, border: `1px solid ${gray[200]}`, boxShadow: shadow.sm }}>
            <Statistic
              title={<span style={{ fontSize: 11, color: gray[400] }}>현재 폴더 크기</span>}
              value={formatBytes(info?.total_size ?? 0)}
              valueStyle={{ fontSize: 22, color: gray[800] }}
              prefix={<DatabaseOutlined style={{ color: state.info, marginRight: 4 }} />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card style={{ borderRadius: radius.lg, border: `1px solid ${gray[200]}`, boxShadow: shadow.sm }}>
            <div style={{ fontSize: 11, color: gray[400], marginBottom: 4 }}>버킷</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: gray[700] }}>{info?.bucket ?? '-'}</div>
            <div style={{ fontSize: 11, color: gray[400] }}>{info?.region ?? '-'}</div>
          </Card>
        </Col>
      </Row>

      {/* 오브젝트 목록 */}
      <div ref={cardWrapRef}>
      <Card
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FolderOutlined style={{ color: brand.primary }} />
            <span style={{ fontSize: 14, fontWeight: 600 }}>오브젝트 목록</span>
            <Breadcrumb
              style={{ marginLeft: 8, fontSize: 13 }}
              items={[
                {
                  title: (
                    <span
                      style={{ cursor: 'pointer', color: currentPrefix ? brand.primary : gray[600] }}
                      onClick={() => navigateTo('')}
                    >
                      <HomeOutlined style={{ marginRight: 4 }} />
                      {info?.bucket ?? 'root'}
                    </span>
                  ),
                },
                ...breadcrumbSegments.map((seg) => ({
                  title: (
                    <span
                      style={{
                        cursor: seg.prefix !== currentPrefix ? 'pointer' : 'default',
                        color: seg.prefix === currentPrefix ? gray[600] : brand.primary,
                        fontWeight: seg.prefix === currentPrefix ? 600 : 400,
                      }}
                      onClick={() => seg.prefix !== currentPrefix && navigateTo(seg.prefix)}
                    >
                      {seg.label}
                    </span>
                  ),
                })),
              ]}
            />
          </div>
        }
        style={{ borderRadius: radius.lg, border: `1px solid ${gray[200]}`, boxShadow: shadow.sm }}
      >
        {/* 일괄 작업 툴바 */}
        {hasSelection && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '8px 12px', marginBottom: 12,
            background: '#eff6ff', border: `1px solid #bfdbfe`,
            borderRadius: radius.md,
          }}>
            <Tag color="blue" style={{ fontWeight: 600, fontSize: 13 }}>
              {selectedKeys.length}개 선택됨
            </Tag>
            {selectedFileCount > 0 && (
              <Tag style={{ fontSize: 12, color: gray[500] }}>
                파일 {selectedFileCount}개 / 폴더 {selectedKeys.length - selectedFileCount}개
              </Tag>
            )}
            <div style={{ flex: 1 }} />
            <Button
              icon={<DownloadOutlined />}
              size="small"
              type="primary"
              ghost
              disabled={selectedFileCount === 0}
              loading={bulkWorking}
              onClick={handleBulkDownload}
            >
              {selectedFileCount}개 다운로드
            </Button>
            <Button
              icon={<DeleteOutlined />}
              size="small"
              danger
              loading={bulkWorking}
              onClick={handleBulkDelete}
            >
              {selectedKeys.length}개 삭제
            </Button>
            <Tooltip title="선택 해제">
              <Button
                size="small" type="text"
                icon={<CloseOutlined />}
                onClick={() => setSelectedKeys([])}
              />
            </Tooltip>
          </div>
        )}

        <div ref={tableWrapRef}>
        <Table
          dataSource={info?.objects ?? []}
          columns={columns}
          rowKey="name"
          loading={loading}
          size="small"
          rowSelection={{
            selectedRowKeys: selectedKeys,
            onChange: (keys) => setSelectedKeys(keys),
            columnWidth: 40,
          }}
          scroll={{ y: bodyHeight }}
          pagination={{
            defaultPageSize: 20, showSizeChanger: true,
            pageSizeOptions: ['20', '50', '100', '200'],
            showTotal: (t, r) => `${r[0]}–${r[1]} / 총 ${t}개`,
          }}
          locale={{ emptyText: '저장된 오브젝트가 없습니다' }}
        />
        </div>
      </Card>
      </div>

      {/* 업로드 모달 */}
      <Modal
        title={
          <Space>
            <UploadOutlined style={{ color: brand.primary }} />
            <span>파일 업로드</span>
            {currentPrefix && <Text style={{ fontSize: 12, color: gray[400] }}>→ {currentPrefix}</Text>}
          </Space>
        }
        open={uploadModal}
        onCancel={() => { if (!uploading) { setPendingFiles([]); setUploadModal(false); } }}
        footer={
          <Space>
            <Button onClick={() => { setPendingFiles([]); setUploadModal(false); }} disabled={uploading}>
              취소
            </Button>
            <Button
              type="primary"
              icon={<UploadOutlined />}
              onClick={handleUpload}
              loading={uploading}
              disabled={!pendingFiles.length}
              style={{ background: brand.primary, borderColor: brand.primary }}
            >
              {pendingFiles.length > 0 ? `${pendingFiles.length}개 업로드` : '업로드'}
            </Button>
          </Space>
        }
        width={520}
      >
        <Upload.Dragger
          multiple
          showUploadList={{ showRemoveIcon: true }}
          fileList={pendingFiles}
          beforeUpload={(file) => {
            setPendingFiles(prev => [...prev, { uid: file.uid, name: file.name, status: 'done', originFileObj: file } as UploadFile]);
            return false;
          }}
          onRemove={(file) => setPendingFiles(prev => prev.filter(f => f.uid !== file.uid))}
          disabled={uploading}
          style={{ marginTop: 8 }}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined style={{ color: brand.primary, fontSize: 36 }} />
          </p>
          <p style={{ fontSize: 14, fontWeight: 600, color: gray[700] }}>파일을 드래그하거나 클릭해 선택</p>
          <p style={{ fontSize: 12, color: gray[400] }}>
            {currentPrefix ? `업로드 위치: ${currentPrefix}` : '루트에 업로드'} · 여러 파일 동시 선택 가능
          </p>
        </Upload.Dragger>
        {uploading && (
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 12, color: gray[500], marginBottom: 4 }}>
              업로드 중... {uploadProgress.current} / {uploadProgress.total}
            </div>
            <Progress
              percent={Math.round((uploadProgress.current / uploadProgress.total) * 100)}
              status="active"
            />
          </div>
        )}
      </Modal>

      {/* 폴더 생성 모달 */}
      <Modal
        title={
          <Space>
            <FolderAddOutlined style={{ color: brand.primary }} />
            <span>폴더 생성</span>
            {currentPrefix && <Text style={{ fontSize: 12, color: gray[400] }}>→ {currentPrefix}</Text>}
          </Space>
        }
        open={folderModal}
        onCancel={() => setFolderModal(false)}
        onOk={handleCreateFolder}
        okText="생성" cancelText="취소"
        confirmLoading={creatingFolder}
        width={400}
      >
        <Form layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            label="폴더명"
            extra={<span style={{ fontSize: 11, color: gray[400] }}>생성 위치: {currentPrefix || '루트'}{folderName ? folderName + '/' : ''}</span>}
          >
            <Input
              placeholder="폴더명 입력"
              value={folderName}
              onChange={(e) => setFolderName(e.target.value)}
              onPressEnter={handleCreateFolder}
              prefix={<FolderOutlined style={{ color: gray[400] }} />}
              autoFocus
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 숨김 다운로드 링크 */}
      <a ref={downloadLinkRef} style={{ display: 'none' }}>dl</a>
    </div>
  );
};

export default StoragePage;
